import React, { useState } from 'react';
import './ElevatorDesign.css';

const elevatorDesignCode = String.raw`
#include <bits/stdc++.h>
using namespace std;

enum class Direction { UP, DOWN, IDLE, EMERGENCY, MAINTENANCE, FIRE };

int elevatorCount = 3;
int floorMax = 9;

enum class MovementStrategy { NORMAL, CHAOTIC };
enum class DispatchStrategy { BEST, WORST };

class Elevator {
    int elevatorId;
    int floor;
    Direction direction;
    map<int, int> requests;
    MovementStrategy strategy; 
    int personLimit;
    int personCount;

public:
    Elevator(int id, MovementStrategy strat, int pLimit) {
        elevatorId = id;
        floor = 0;
        direction = Direction::IDLE;
        requests = {};
        strategy = strat;
        personLimit = pLimit;
        personCount = 0;
    }
    
    int getId() const { return elevatorId; }
    int getFloor() const { return floor; }    
    Direction getDirection() const { return direction; }
    int getPersonLimit() const { return personLimit; }
    int getPersonCount() const { return personCount; }
    
    void setEmergency() {
        direction = Direction::EMERGENCY;
        requests.clear();
        personCount = 0;
        cout << "[EMERGENCY] Elevator " << elevatorId << " halted safely at floor " << floor << "! Switching to MAINTENANCE required.\n";
    }

    void setFire() {
        direction = Direction::FIRE;
        requests.clear();
        personCount = 0;
        cout << "[FIRE ALARM] Elevator " << elevatorId << " is overriding all requests and returning to Floor 0!\n";
    }

    void setMaintenance() {
        if (direction == Direction::EMERGENCY || direction == Direction::FIRE) {
            direction = Direction::MAINTENANCE;
            cout << "[MAINTENANCE] Elevator " << elevatorId << " is now under technician inspection.\n";
        } else {
            cout << "Elevator " << elevatorId << " must be in Emergency or Fire mode before entering Maintenance.\n";
        }
    }

    void resetToNormal() {
        if (direction == Direction::MAINTENANCE || direction == Direction::EMERGENCY) {
            direction = Direction::IDLE;
            requests.clear();
            personCount = 0;
            cout << "[RESET] Elevator " << elevatorId << " has cleared inspection and is back to NORMAL (IDLE).\n";
        } else {
            cout << "Elevator " << elevatorId << " is already operational.\n";
        }
    }

    void addFloor(int floorId, int count) { 
        if (direction == Direction::EMERGENCY || direction == Direction::MAINTENANCE || direction == Direction::FIRE) {
            cout << "Request rejected: Elevator " << elevatorId << " is out of service.\n";
            return;
        }
        requests[floorId] += count;
        personCount += count;
    }

    void removeFloor(int floorId) { 
        if (requests.count(floorId)) {
            personCount -= requests[floorId];
            requests.erase(floorId); 
            cout << "[CANCEL] Floor " << floorId << " request successfully removed from Elevator " << elevatorId << ".\n";
        } else {
            cout << "[CANCEL] Floor " << floorId << " not found in Elevator " << elevatorId << " requests.\n";
        }
    }
    
    void tick() {
        if (direction == Direction::EMERGENCY || direction == Direction::MAINTENANCE) {
            return;
        }
        if (direction == Direction::FIRE) {
            if (floor > 0) {
                floor--;
                cout << "  [FIRE EVACUATION] Elevator " << elevatorId << " descending to floor " << floor << endl;
            }
            if (floor == 0) {
                cout << "  [FIRE EVACUATION] Elevator " << elevatorId << " reached ground floor! Doors open. Auto-switching to MAINTENANCE.\n";
                direction = Direction::MAINTENANCE;
            }
            return;
        }

        if (strategy == MovementStrategy::NORMAL) tickNormal();
        else tickChaotic();
    }

private:
    void tickNormal() {
        if (requests.count(floor)) {
            personCount -= requests[floor];
            requests.erase(floor);
            cout << "  [Door] Elevator " << elevatorId << " opened at floor " << floor << ". Current passengers: " << personCount << "/" << personLimit << endl;
            if (requests.empty()) direction = Direction::IDLE;
            return;
        }
        if (requests.empty()) {
            direction = Direction::IDLE;
            return;
        }
        if (direction == Direction::IDLE) {
            int targetFloor = requests.begin()->first;
            direction = (targetFloor > floor) ? Direction::UP : Direction::DOWN;
        }
        
        if (direction == Direction::UP) {
            if (floor < floorMax) { floor++; cout << "  [Move] Elevator " << elevatorId << " went UP to floor " << floor << endl; }
            else { direction = Direction::DOWN; floor--; }
        } else if (direction == Direction::DOWN) {
            if (floor > 0) { floor--; cout << "  [Move] Elevator " << elevatorId << " went DOWN to floor " << floor << endl; }
            else { direction = Direction::UP; floor++; }
        }
    }

    void tickChaotic() {
        if (requests.count(floor)) {
            personCount -= requests[floor];
            requests.erase(floor);
            cout << "  [Door] Chaotic Elevator " << elevatorId << " opened at floor " << floor << endl;
            if (requests.empty()) direction = Direction::IDLE;
            return;
        }
        if (requests.empty()) {
            direction = Direction::IDLE;
            return;
        }
        
        int maxDist = -1, targFloor = floor;
        for (auto &pair : requests) {
            if (abs(floor - pair.first) > maxDist) {
                maxDist = abs(floor - pair.first);
                targFloor = pair.first;
            }
        }
        direction = (targFloor < floor) ? Direction::DOWN : Direction::UP;
        if (direction == Direction::UP) { floor++; cout << "  [Chaotic Move] Elevator " << elevatorId << " went UP to " << floor << endl; }
        else { floor--; cout << "  [Chaotic Move] Elevator " << elevatorId << " went DOWN to " << floor << endl; }
    }
};

class ElevatorController {
    vector<Elevator> elevators;
    DispatchStrategy dispatchStrategy; 

public:
    ElevatorController(int numElevators, DispatchStrategy dStrat, MovementStrategy mStrat, int pLimit) {
        dispatchStrategy = dStrat;
        for (int i = 1; i <= numElevators; i++) {
            elevators.push_back(Elevator(i, mStrat, pLimit));
        }
    }
    
    int getDistance(Direction dir1, int floor1, Direction dir2, int floor2) {
        if (dir1 == Direction::UP) {
            if (dir2 == Direction::UP) return (floor1 >= floor2) ? floor1 - floor2 : 100 + abs(floor1 - floor2);
            else if (dir2 == Direction::IDLE) return abs(floor1 - floor2);
            else return 10 + floor1 + floor2;
        } else {
            if (dir2 == Direction::DOWN) return (floor1 <= floor2) ? floor2 - floor1 : 100 + floor1 - floor2;
            else if (dir2 == Direction::IDLE) return abs(floor1 - floor2);
            else return 10 + floor1 + floor2;
        }
    }
    
    void callElevator(int floor, Direction dir, int personCount) {
        if (floor < 0 || floor > floorMax) return;
        
        int bestElevatorIdx = -1;
        int targetMetric = (dispatchStrategy == DispatchStrategy::BEST) ? 1e9 : -1;

        for (int i = 0; i < elevators.size(); i++) {
            Direction eDir = elevators[i].getDirection();
            if (eDir == Direction::EMERGENCY || eDir == Direction::MAINTENANCE || eDir == Direction::FIRE) continue;
            
            int freeSpots = elevators[i].getPersonLimit() - elevators[i].getPersonCount();
            if (freeSpots < personCount) continue;

            int distanceToReach = getDistance(dir, floor, eDir, elevators[i].getFloor());
            
            if (dispatchStrategy == DispatchStrategy::BEST) {
                if (distanceToReach < targetMetric) { targetMetric = distanceToReach; bestElevatorIdx = i; }
            } else { 
                if (distanceToReach > targetMetric) { targetMetric = distanceToReach; bestElevatorIdx = i; }
            }
        }
        
        if (bestElevatorIdx == -1) {
            cout << "Elevator call failed: No available operational elevator.\n";
            return;
        }
        
        elevators[bestElevatorIdx].addFloor(floor, personCount);
        cout << "Elevator call success: Elevator " << elevators[bestElevatorIdx].getId() << " assigned.\n";
    }

    void pressFloor(int elevatorId, int floor, int count) {
        Direction eDir = elevators[elevatorId-1].getDirection();
        if (eDir == Direction::EMERGENCY || eDir == Direction::MAINTENANCE || eDir == Direction::FIRE) {
            cout << "Ignored: Elevator " << elevatorId << " buttons are disabled.\n";
            return;
        }
        elevators[elevatorId-1].addFloor(floor, count);
        cout << "Floor " << floor << " pressed inside elevator " << elevatorId << " (Count: " << count << ")\n";
    }

    void cancelFloorRequest(int elevatorId, int floor) {
        elevators[elevatorId - 1].removeFloor(floor);
    }
    
    void triggerEmergency(int id) { elevators[id - 1].setEmergency(); }
    void triggerFire(int id) { elevators[id - 1].setFire(); }
    void setMaintenance(int id) { elevators[id - 1].setMaintenance(); }
    void resetElevator(int id) { elevators[id - 1].resetToNormal(); }
    
    void printStatus() {
        cout << "\n--- SYSTEM STATUS REPORT ---\n";
        for (const auto& e : elevators) {
            cout << "Elevator " << e.getId() 
                 << " | Floor: " << e.getFloor() 
                 << " | Limit: " << e.getPersonLimit() 
                 << " | Current Load: " << e.getPersonCount() << "\n";
        }
        cout << "----------------------------\n";
    }

    void simulateTick() {
        for (auto &e : elevators) e.tick();
    }
};

int main() {
    cout << "========================================================\n";
    cout << "      COMPREHENSIVE NORMAL MODE TEST DRIVER             \n";
    cout << "========================================================\n\n";

    // Setup: 3 elevators, BEST dispatch strategy, NORMAL movement, max capacity 5
    ElevatorController controller(3, DispatchStrategy::BEST, MovementStrategy::NORMAL, 5);

    int globalTime = 0;
    auto runTicks = [&](int ticks, const string& phaseDesc) {
        cout << "\n>>> PHASE: " << phaseDesc << " <<<\n";
        for (int i = 0; i < ticks; i++) {
            globalTime++;
            cout << "[Tick " << globalTime << "]\n";
            controller.simulateTick();
        }
    };

    // 1. Test Getters and Initial State
    controller.printStatus();

    // 2. Test callElevator & Normal Movement (Pickup)
    cout << "\n[Action] Calling Elevator for 2 people at Floor 3 (Going UP)...\n";
    controller.callElevator(3, Direction::UP, 2);
    runTicks(4, "Elevator traveling to pick up passengers at Floor 3");

    // 3. Test pressFloor & Inside Destination Handling
    cout << "\n[Action] Passengers inside Elevator 1 press Floor 7...\n";
    controller.pressFloor(1, 7, 2);
    runTicks(4, "Elevator 1 traveling to destination Floor 7");

    // 4. Test removeFloor (Canceling a request)
    cout << "\n[Action] Calling Elevator 2 to Floor 4, then canceling request...\n";
    controller.callElevator(4, Direction::DOWN, 1);
    controller.cancelFloorRequest(2, 4);
    runTicks(2, "Verifying canceled request is ignored");

    // 5. Test Capacity Constraint Check
    cout << "\n[Action] Testing strict capacity limits (Calling for 6 people when max is 5)...\n";
    controller.callElevator(2, Direction::UP, 6);

    // 6. Test Emergency, Maintenance, and Reset Lifecycle
    cout << "\n[Action] Triggering Emergency on Elevator 3...\n";
    controller.triggerEmergency(3);
    
    cout << "[Action] Attempting button press on Emergency Elevator 3 (should be ignored)...\n";
    controller.pressFloor(3, 5, 1);

    cout << "[Action] Transitioning Elevator 3 from Emergency to Maintenance...\n";
    controller.setMaintenance(3);

    cout << "[Action] Resetting Elevator 3 back to Normal operational state...\n";
    controller.resetElevator(3);

    // 7. Test Fire Alarm Protocol
    cout << "\n[Action] Sending Elevator 1 to Floor 5 to prepare for Fire Test...\n";
    controller.callElevator(5, Direction::DOWN, 1);
    runTicks(5, "Elevator 1 moving to Floor 5");

    cout << "[Action] Triggering FIRE ALARM on Elevator 1...\n";
    controller.triggerFire(1);
    runTicks(6, "Elevator 1 executing Fire Evacuation Protocol to Floor 0");

    // Final Status Check
    controller.printStatus();

    cout << "\n========================================================\n";
    cout << "      NORMAL MODE TESTING COMPLETE SUCCESSFUL           \n";
    cout << "========================================================\n";


    // ==========================================================
    // CHAOTIC MODE TEST DRIVER
    // ==========================================================
    cout << "\n\n========================================================\n";
    cout << "   WELCOME TO DOOM ELEVATORS LTD. - CHAOTIC MODE DRIVER \n";
    cout << "   Strategy: WORST | Movement: CHAOTIC | Safety: NONE     \n";
    cout << "========================================================\n\n";

    // Setup: 3 elevators, WORST dispatch strategy (furthest elevator), CHAOTIC movement (furthest floor first), max capacity 5
    ElevatorController chaoticController(3, DispatchStrategy::WORST, MovementStrategy::CHAOTIC, 5);

    int chaoticGlobalTime = 0;
    auto runChaoticTicks = [&](int ticks, const string& phaseDesc) {
        cout << "\n>>> PHASE: " << phaseDesc << " <<<\n";
        for (int i = 0; i < ticks; i++) {
            chaoticGlobalTime++;
            cout << "[Chaotic Tick " << chaoticGlobalTime << "]\n";
            chaoticController.simulateTick();
        }
    };

    // 1. Test Status & Worst Dispatch Strategy
    chaoticController.printStatus();
    
    cout << "\n[Action] Calling elevator for 1 person at Floor 3 with WORST dispatch...\n";
    chaoticController.callElevator(3, Direction::UP, 1);

    // 2. Test Chaotic Movement (Ignoring closer targets, hunting the furthest floor)
    cout << "\n[Action] Passengers inside Elevator 3 press multiple floors (1, 3, and 9)...\n";
    chaoticController.pressFloor(3, 1, 1);
    chaoticController.pressFloor(3, 3, 1);
    chaoticController.pressFloor(3, 9, 1);
    
    runChaoticTicks(5, "Chaotic elevator ignoring floors 1 and 3 to chase Floor 9 first");

    // 3. Test removeFloor in Chaotic Mode
    cout << "\n[Action] Adding chaotic requests to Elevator 2 and cancelling one...\n";
    chaoticController.pressFloor(2, 2, 1);
    chaoticController.pressFloor(2, 8, 1);
    chaoticController.cancelFloorRequest(2, 8); // Cancel the furthest target!
    runChaoticTicks(3, "Elevator 2 recalculating path after cancellation");

    // 4. Test Fire Protocol Vaporizer (personCount = 0 and requests cleared mid-way)
    cout << "\n[Action] Filling Elevator 1 with 4 people and sending them to Floor 7...\n";
    chaoticController.pressFloor(1, 7, 4);
    runChaoticTicks(2, "Elevator 1 moving up with passengers...");

    cout << "\n[Action] 🚨 EMERGENCY FIRE ALARM TRIGGERED FOR ELEVATOR 1 🚨\n";
    cout << "[System Note] Code executes requests.clear() and personCount = 0.\n";
    chaoticController.triggerFire(1);
    
    runChaoticTicks(3, "Elevator 1 dragging empty ghost cabin back down to Floor 0");

    // 5. Test Emergency & Maintenance recovery cycle in Chaos
    cout << "\n[Action] Triggering Emergency on Elevator 2 during ongoing chaos...\n";
    chaoticController.triggerEmergency(2);

    cout << "[Action] Technician arrives, setting Elevator 2 to Maintenance...\n";
    chaoticController.setMaintenance(2);

    cout << "[Action] Resetting Elevator 2 back into the chaos...\n";
    chaoticController.resetElevator(2);

    // Final Status Report
    chaoticController.printStatus();

    cout << "\n========================================================\n";
    cout << "   CHAOTIC SIMULATION COMPLETE. PLEASE HIRE A LAWYER.   \n";
    cout << "========================================================\n";

    return 0;
}

`;


function ElevatorDesign({ theme, onBackClick }) {
    const [copied, setCopied] = useState(false);

    const handleCopyCode = async () => {
        try {
            await navigator.clipboard.writeText(elevatorDesignCode);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1800);
        } catch (error) {
            console.error('Copy failed:', error);
            setCopied(false);
        }
    };

    return (
        <div className={`app ${theme}`}>
            <main className="system-design-page">
                <section className="system-designs-header-section">
                    <div className="system-designs-header">
                        <div className="system-designs-header-copy">
                            <h1>Elevator Design</h1>
                            <p className="system-designs-subtitle">Elevator go up, down, maybe round and round. lol.
                            </p>
                        </div>
                        <button className="back-btn" onClick={onBackClick}>
                            Back to Designs
                        </button>
                    </div>
                </section>

                <section className="system-designs-content">
                    <div className="system-design-article">
                        <div className="design-section">
                            <h2>Functional Requirements</h2>
                            <ul>
                                <li>Have a group of elevators in the building.</li>
                                <li>Elevators move up/down floors or remain stationary.</li>
                                <li>Up/down buttons can be pressed from outside the elevator on any floor.</li>
                                <li>Inside the elevator, floor selection is made at any time.</li>
                                <li>The system should always choose the fastest or most suitable elevator for a request.</li>
                                <li>Capacity must be enforced so a call is rejected if the elevator is already full.</li>
                                <li>Emergency and fire states must override normal operations and stop standard request handling.</li>
                                <li>Maintenance mode should only be entered through a valid emergency/fire transition before reset.</li>
                                <li>VIP or staff-only floor logic can be modeled as special requests or restricted elevator assignment rules.</li>
                                <li>Dedicated service elevators or employee-only floors can be represented as extra dispatch constraints, when needed.</li>
                            </ul>
                        </div>

                        <div className="design-section">
                            <h2>Design</h2>
                            <ul>
                                <li>3 elevators, 10 floors from 0 to 9.</li>
                                <li>Request format: elevator call with direction and floor.</li>
                                <li>For outside calls, direction is known: up or down.</li>
                                <li>For inside calls, the chosen destination floor is known.</li>
                                <li>Simulation runs in ticks up to 40.</li>
                                <li>Each request is routed to the fastest-arriving elevator available.</li>
                                <li>Elevators continue traveling in one direction until the min or max floor is reached, then become idle or flip direction.</li>
                                <li>Good logic: the controller filters out emergency, maintenance, and fire elevators before assignment, keeping safety-critical states protected.</li>
                                <li>Good logic: in normal mode, the distance calculation prefers the least-delay elevator rather than just the first available one.</li>
                                <li>Good logic: canceled requests remove stale floor entries and reduce phantom passenger counts.</li>
                            </ul>
                        </div>

                        <div className="design-section stupid-design-section">
                            <h2>Stupid Design</h2>
                            <ul>
                                <li>Chaotic mode picks the request with the maximum distance from the current floor instead of the nearest one.</li>
                                <li>It sets direction toward the farthest target, so nearby requests are ignored while the elevator deliberately chases the worst possible destination.</li>
                                <li>The WORST dispatch strategy assigns the farthest elevator, maximizing passenger wait time instead of minimizing it.</li>
                                <li>In fire mode, requests are cleared and personCount resets to zero, turning an evacuation system into a dramatic and completely broken reset button.</li>
                                <li>This is the intentionally bad logic: a system that acts like it was designed by a committee of confused raccoons.</li>
                            </ul>
                        </div>

                        <div className="design-section">
                            <h2>Main Entities</h2>
                            <ul>
                                <li>Enum Direction: UP, DOWN, IDLE</li>
                                <li>Class Elevator</li>
                                <li>Class ElevatorController</li>
                                <li>Integer floor in range 0 to 9</li>
                            </ul>
                        </div>

                        <div className="design-section">
                            <div className="design-code-header">
                                <h2>C++ Sample</h2>
                                <button type="button" className="copy-code-btn" onClick={handleCopyCode}>
                                    {copied ? 'Copied!' : 'Copy code'}
                                </button>
                            </div>
                            <pre className="design-code-block"><code>{elevatorDesignCode}</code></pre>
                        </div>
                    </div>

                    <div className="system-designs-footer-note">
                        Try it out, have fun!!
                    </div>

                    <button className="back-btn" onClick={onBackClick}>
                        Back to Designs
                    </button>
                </section>
            </main>

            <footer className="footer">
                <p>© 2026 StupidDesigns.com. All rights reserved. Not that anyone actually wants them.</p>
            </footer>
        </div>
    );
}

export default ElevatorDesign;
